/* eslint-disable no-restricted-syntax */
import { SeoController, useSocialShareModal } from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import {
  emailShareUrl,
  facebookShareUrl,
  twitterShareUrl,
  useRouter,
  useSlideshow,
  waShareUrl,
} from '@application/utils';
import { PLACEHOLDER_IMAGE_SHELTER } from '@presentation/constants';
import { ShelterPage } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormatArticles } from '../ArticleListPageController/api/useFormatArticles';
import { useFormatShelters } from '../ShelterListPageController/api/useFormatShelters';
import { useFormatShelter } from './api/useFormatShelter';
import { useGetArticles } from './api/useGetArticles';
import { useGetShelter } from './api/useGetShelter';
import { useGetShelters } from './api/useGetShelters';
import { useRelatedLinks } from './api/useRelatedLinks';

const DEFAULT_IMAGES = [
  {
    large: PLACEHOLDER_IMAGE_SHELTER,
    small: PLACEHOLDER_IMAGE_SHELTER,
    medium: PLACEHOLDER_IMAGE_SHELTER,
  },
];

export const ShelterPageController: FC = () => {
  const { infoFromCurrentPath } = useRouter();
  const { translate } = useTranslation();
  const { pathname } = useLocation();
  const { show: showShareModal, setUrls } = useSocialShareModal();

  const shelterUrl = infoFromCurrentPath.params[0];
  const { shelter, shelterLoading } = useGetShelter(shelterUrl);
  const { formatShelter } = useFormatShelter();
  const formattedShelter = formatShelter(shelter);

  const { shelters, sheltersLoading } = useGetShelters(shelterUrl);
  const { formattedShelters } = useFormatShelters(shelters);

  const { articles, articlesLoading } = useGetArticles();
  const { formattedArticles } = useFormatArticles(articles);

  const { relatedLinks } = useRelatedLinks(shelterUrl);

  const slideshowItems = (
    shelter?.imgs && shelter.imgs.length > 0 ? shelter?.imgs : DEFAULT_IMAGES
  ).map((img, index) => ({
    src: (img.medium ?? img.large) as string,
    thumbnail: img.small as string,
    alt: translate(`shelterDetails.imageAlts.${index}`, '').replace('$name', shelter?.name ?? ''),
  }));

  const title = translate('shelterDetails.dynamicSeo.title')
    .replace('$name', shelter?.name ?? '')
    .replace('$country', translate(`enum.country.${shelter?.address?.country}`, ''));

  const description = translate('shelterDetails.dynamicSeo.description')
    .replace('$name', shelter?.name ?? '')
    .replace('$location', shelter?.address?.city ?? '')
    .replace('$country', translate(`enum.country.${shelter?.address?.country}`, ''));

  const socialShareClickHandler = useCallback(() => {
    const url = `https://tipaw.com${pathname}`;

    showShareModal();

    setUrls({
      facebook: facebookShareUrl(url),
      twitter: twitterShareUrl(url, formattedShelter?.name ?? ''),
      whatsapp: waShareUrl(url),
      email: emailShareUrl(
        url,
        formattedShelter?.name ?? '',
        `${formattedShelter?.name ?? ''}\n\n${formattedShelter?.description}`,
      ),
    });
  }, [formattedShelter?.description, formattedShelter?.name, pathname, setUrls, showShareModal]);

  return (
    <>
      <SeoController title={title} description={description} thumbnail={slideshowItems[0].src} />

      {!shelter && !shelterLoading && <RedirectWithStatus status={404} to="/404" />}

      <ShelterPage
        shelterLoading={shelterLoading}
        shelterData={formatShelter(shelter)}
        slideshowData={useSlideshow(slideshowItems)}
        articleItems={formattedArticles}
        articleItemsLoading={articlesLoading}
        relatedLinks={relatedLinks}
        shelterItems={formattedShelters}
        shelterItemsLoading={sheltersLoading}
        onSocialShareClick={socialShareClickHandler}
      />
    </>
  );
};
